import Header from 'components/Header'
import Thirdparty from 'components/service/Thirdparty'
import { publicConfig } from 'config'
import { getds } from 'ds'
import { useViewCounter } from 'hooks/use-view-counter'
import dynamic from 'next/dynamic'
import { useCallback, useEffect } from 'react'
import { CommonDataContext } from 'shared-components/contexts/CommonDataContext'
import { ConversationContext } from 'shared-components/comments/conversation/ConversationContext'
import { PollStoreContext, PollStoreProps } from 'shared-components/polls/PollStoreContext'
import { CommentHubAPIContext } from 'shared-components/contexts/CommentHubAPIContext'
import { DealBarContext } from 'shared-components/contexts/DealBarContext'
import { NotificationsContext } from 'shared-components/contexts/NotificationsContext'
import { PlaywireContext } from 'shared-components/contexts/PlaywireContext'
import { PostContext } from 'shared-components/contexts/PostContext'
import { ThemeContext } from 'shared-components/contexts/ThemeContext'
import { UIVariantContext } from 'shared-components/contexts/UIVariantContext'
import { defaultAdInferePath } from 'shared-components/hooks/use-ad-path'
import { useIsPreview } from 'shared-components/hooks/use-is-preview'
import Footer from 'shared-components/layout/Footer'
import PageTransition from 'shared-components/layout/PageTransition'
import PopupWrapper from 'shared-components/layout/PopupWrapper'
import UnderNavBlock from 'shared-components/layout/UnderNavBlock'
import AdPath from 'shared-components/service/AdPath'
import styleUtils from 'shared-components/styles/util.module.css'
import Logo from 'shared-svg/AuthorityMedia.svg'
import { DSGetDealBar } from 'types.aa.ds'
import styles from './Layout.module.css'
import Main from 'shared-components/layout/Main'
import { SearchContext } from 'shared-components/contexts/SearchContext'
import { DSLayoutProps } from 'shared-definitions/types'
import { useGlobalCdpClient } from 'shared-components/hooks/use-global-cdp-client'
import { useClearbuyCookies } from 'shared-components/hooks/use-clearbuy-cookies'
import { MyDevicesContext } from 'lib/devices/MyDevicesContext'
import { ViewerDevicesBridge } from 'components/devices/ViewerDevicesBridge'

const AdminPanel = dynamic(() => import('shared-components/layout/AdminPanel'), { ssr: false })
const NotificationsLayer = dynamic(() => import('shared-components/layout/NotificationsLayer'))
// Sign-in surface. ssr: false because it measures its container to size the
// Google-owned button iframe, which only exists in the browser.
const LoginDialog = dynamic(() => import('shared-components/ui/LoginDialog'), { ssr: false })
// "Which phone do you use?" after sign-in (docs/your-device-plan.md): client-only like the
// sign-in dialog — server HTML never knows the reader.
const DeviceStep = dynamic(() => import('components/devices/DeviceStep'), { ssr: false })

// The one poll store's WordPress calls (polls in the conversation): the in-article poll and the
// conversation panel's read and write the same store, mounted around the page below.
const pollStoreProps: PollStoreProps = {
  sendPoll: async feed => (await getds()).sendPoll(feed),
  getPoll: async pollId => (await getds()).getPoll(pollId),
}

const Layout: React.FC<React.PropsWithChildren & { layoutProps: DSLayoutProps }> = ({
  children,
  layoutProps,
}) => {
  const isPreview = useIsPreview()
  const { queue } = NotificationsContext.useContainer()
  const { postId } = PostContext.useContainer()
  const { ready, theme } = ThemeContext.useContainer()
  const {
    showAdminPanel,
    // common: { dealBar },
  } = CommonDataContext.useContainer()
  useEffect(() => {
    if (!ready) {
      return
    }

    document.body.classList[theme === 'dark' ? 'add' : 'remove'](styleUtils.darkTheme)
    document.body.dataset.currentTheme = theme
  }, [ready, theme])

  useViewCounter(postId)

  useGlobalCdpClient({
    eventsCollectorUrl: publicConfig('cdpEventsCollectorUrl'),
  })
  useClearbuyCookies({
    clearbuyCookieUrl: publicConfig('clearbuyCookieUrl'),
  })

  const getDealbar = useCallback(async (...args: Parameters<DSGetDealBar>) => {
    const dataSource = await getds()
    return dataSource.getDealBar(...args)
  }, [])
  return (
    <PlaywireContext.Provider
      initialState={{
        inst: null,
        enabled: !publicConfig('adsDisabled'),
        adClassList: publicConfig('adClassList'),
      }}
    >
      <SearchContext.Provider
        initialState={{
          productsSuggestions: async (...args) => {
            const dataSource = await getds()
            return dataSource.getProductsSuggestions(...args)
          },
          search: async (...args) => {
            const dataSource = await getds()
            return dataSource.getSearchPage(...args)
          },
        }}
      >
        <CommentHubAPIContext.Provider
          initialState={{
            apiUrl: publicConfig('commentHubApiUrl'),
            site: publicConfig('commentHubSite'),
            appUrl: publicConfig('appUrl'),
            // Emoji reactions under comments in place of the thumbs (2026-10-29), as on SoundGuys.
            reactions: true,
          }}
        >
          <MyDevicesContext.Provider>
            <ViewerDevicesBridge>
              <ConversationContext.Provider>
                <UIVariantContext.Provider
                  initialState={{
                    colors: 'androidauthority',
                    postPreviewScore: 'line',
                    buttonStyle: 'fill',
                  }}
                >
                  <DealBarContext.Provider initialState={{ getDealbar, dealbar: null }}>
                    <AdPath
                      inferePath={(path, tags) => {
                        if (path === '/') {
                          return '/'
                        }
                        return defaultAdInferePath(path, tags)
                      }}
                    />
                    {showAdminPanel ? (
                      <AdminPanel editPostTpl={publicConfig('postEditLink')} />
                    ) : null}
                    <Header />
                    <UnderNavBlock tocVisibility={layoutProps?.tocVisibility} />
                    <PollStoreContext.Provider initialState={pollStoreProps}>
                      <Main className={styles.main}>{children}</Main>
                    </PollStoreContext.Provider>
                    <Footer logo={Logo} logoLink={{ href: publicConfig('authorityMediaLink') }} />
                    <PopupWrapper />
                    <LoginDialog />
                    <DeviceStep />
                    <PageTransition />
                    {queue.length ? <NotificationsLayer /> : null}
                    {!isPreview ? <Thirdparty /> : null}
                  </DealBarContext.Provider>
                </UIVariantContext.Provider>
              </ConversationContext.Provider>
            </ViewerDevicesBridge>
          </MyDevicesContext.Provider>
        </CommentHubAPIContext.Provider>
      </SearchContext.Provider>
    </PlaywireContext.Provider>
  )
}

export default Layout
